import React, { useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { getLaborHours } from "../../../fetchers/cmmsWorkOrder";
import LaborHourEditDialog from "./LaborHourEditDialog";
import { formatTimestamp, formatDurationHours } from "../../cmms/cmmsFormat";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_TECHNICIAN_ROLES } from "../../cmms/cmmsRoles";
import messages from "../messages";
import { CmmsListSkeleton, CmmsErrorState } from "../../cmms/components/CmmsStates";

const computeDurationHours = (checkInAt, checkOutAt) => {
  if (!checkInAt || !checkOutAt) return null;
  const start = new Date(checkInAt).getTime();
  const end = new Date(checkOutAt).getTime();
  if (!start || !end || end < start) return null;
  return (end - start) / 3600000;
};

// null means checked in but not out yet. "Active" was hardcoded English here and never
// went through intl, even though messages.laborActive already existed.
const formatDuration = (intl, hours) =>
  hours === null ? intl.formatMessage(messages.laborActive) : formatDurationHours(intl, hours);

export default function LaborHourList({ workOrderId, companyKey, userNameById }) {
  const intl = useIntl();
  const loadFailed = intl.formatMessage(messages.laborLoadFailed);
  const [editing, setEditing] = useState(null);

  const query = useQuery(
    ["cmmsWorkOrderLabor", companyKey, workOrderId],
    () => getLaborHours(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 15000 },
  );

  const laborHours = (query.data ?? [])
    .slice()
    .sort((a, b) => new Date(b.checkInAt).getTime() - new Date(a.checkInAt).getTime());

  const totalHours = laborHours.reduce((sum, lh) => {
    const duration = computeDurationHours(lh.checkInAt, lh.checkOutAt);
    return duration === null ? sum : sum + duration;
  }, 0);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <FormattedMessage {...messages.laborCount} values={{ count: laborHours.length }} />
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          <FormattedMessage {...messages.laborTotal} values={{ duration: formatDurationHours(intl, totalHours) }} />
        </Typography>
      </Stack>

      {query.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }
      {query.error &&
        <CmmsErrorState error={query.error} fallback={loadFailed} onRetry={() => query.refetch()} isRetrying={query.isFetching} dense />
      }
      {!query.isLoading && !query.error && laborHours.length === 0 &&
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          <FormattedMessage {...messages.laborEmpty} />
        </Typography>
      }
      {laborHours.length > 0 &&
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage {...messages.columnUser} /></TableCell>
              <TableCell><FormattedMessage {...messages.columnCheckIn} /></TableCell>
              <TableCell><FormattedMessage {...messages.columnCheckOut} /></TableCell>
              <TableCell align="right"><FormattedMessage {...messages.columnDuration} /></TableCell>
              <TableCell><FormattedMessage {...messages.fieldNotes} /></TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {laborHours.map((lh) => {
              const user = userNameById?.get(lh.userId) ?? `#${lh.userId}`;
              const duration = computeDurationHours(lh.checkInAt, lh.checkOutAt);
              return (
                <TableRow key={lh.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{user}</span>
                      {lh.manualOverride &&
                        <Chip label={intl.formatMessage(messages.laborManualOverride)} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                      }
                    </Stack>
                  </TableCell>
                  <TableCell>{formatTimestamp(intl, lh.checkInAt)}</TableCell>
                  <TableCell>{formatTimestamp(intl, lh.checkOutAt)}</TableCell>
                  <TableCell align="right">{formatDuration(intl, duration)}</TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{lh.notes ?? "—"}</TableCell>
                  <TableCell>
                    {/* Labour hours are technician work. NOTE: the backend PUT
                        {id}/labor/{laborHourId} carries no policy of its own, so this gate
                        is UI tidiness rather than enforcement — recorded as a finding. */}
                    <HideIfNoRole anyOf={CMMS_TECHNICIAN_ROLES}>
                      <IconButton size="small" aria-label={intl.formatMessage(messages.laborEditTitle)} onClick={() => setEditing(lh)}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </HideIfNoRole>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      }

      <LaborHourEditDialog
        open={Boolean(editing)}
        laborHour={editing}
        workOrderId={workOrderId}
        companyKey={companyKey}
        onClose={() => setEditing(null)}
        onSuccess={async () => {
          setEditing(null);
          await query.refetch();
        }}
      />
    </Box>
  );
}
